/**
 * Sidebar collapse + compact-rail state, persisted to localStorage so a
 * user's choices survive reloads. `defaultOpen` from nav-config applies on
 * first load only — a remembered override always wins (never clobbered).
 */
import { useCallback, useEffect, useState } from "react";

import { SECTIONS, type NavSection } from "./nav-config";

const SECTIONS_KEY = "hwb:nav:open"; // { [sectionOrSubgroupId]: boolean } overrides
const COMPACT_KEY = "hwb:nav:compact"; // "1" | "0"

type OpenMap = Record<string, boolean>;

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Seed defaults from config for every section + subgroup. */
function seedDefaults(): OpenMap {
  const map: OpenMap = {};
  for (const section of SECTIONS as NavSection[]) {
    map[section.id] = section.defaultOpen;
    for (const item of section.items) {
      if ("kind" in item && item.kind === "subgroup") {
        map[item.id] = item.defaultOpen;
      }
    }
  }
  return map;
}

export interface SidebarState {
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  compact: boolean;
  setCompact: (v: boolean) => void;
  toggleCompact: () => void;
}

export function useSidebarState(): SidebarState {
  const [open, setOpen] = useState<OpenMap>(() => ({
    ...seedDefaults(),
    ...readJSON<OpenMap>(SECTIONS_KEY, {}),
  }));
  const [compact, setCompactState] = useState<boolean>(
    () => localStorage.getItem(COMPACT_KEY) === "1",
  );

  // Persist only the user's explicit overrides, not the seeded defaults, so a
  // future change to a section's defaultOpen still takes effect for untouched
  // sections.
  useEffect(() => {
    const defaults = seedDefaults();
    const overrides: OpenMap = {};
    for (const [id, value] of Object.entries(open)) {
      if (defaults[id] !== value) overrides[id] = value;
    }
    try {
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(overrides));
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [open]);

  useEffect(() => {
    try {
      localStorage.setItem(COMPACT_KEY, compact ? "1" : "0");
    } catch {
      /* non-fatal */
    }
  }, [compact]);

  const isOpen = useCallback((id: string) => open[id] ?? false, [open]);
  const toggle = useCallback(
    (id: string) => setOpen((prev) => ({ ...prev, [id]: !(prev[id] ?? false) })),
    [],
  );
  const setCompact = useCallback((v: boolean) => setCompactState(v), []);
  const toggleCompact = useCallback(() => setCompactState((v) => !v), []);

  return { isOpen, toggle, compact, setCompact, toggleCompact };
}
